import type { LeadRow, OrderBrief } from "./api/types";
import type { ShellIconName } from "./components/ShellIcons";
import { orderStage } from "./orders";

/**
 * The customer table's arithmetic: the shop's calendar, the date filters, and
 * the order a colleague works through the day in. No React here, so the rules
 * the page shows can be read in one place.
 */

export const HOUR = 36e5;
export const DAY = 864e5;

/** Whole days since the epoch on the shop's calendar (Malaysia, UTC+8, no daylight saving). */
export const shopDay = (ms: number) => Math.floor((ms + 8 * HOUR) / DAY);
export const todayNo = () => shopDay(Date.now());

/** The shop day an ISO time falls on, or NaN. */
export function dayNo(iso?: string): number {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isNaN(t) ? NaN : shopDay(t);
}

/** "2026-09-15", a date picked in an input, as a shop day. */
export function dateInputDay(s: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / DAY : NaN;
}

/** A shop day as "2026-09-15", for a date input. */
export const dayInput = (n: number) => new Date(n * DAY).toISOString().slice(0, 10);

const WEEKDAY = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"];

/** 9月17日, or 9月17日 周四. */
export function dateOfDay(n: number, weekday = false): string {
  const d = new Date(n * DAY);
  const s = `${d.getUTCMonth() + 1}月${d.getUTCDate()}日`;
  return weekday ? `${s} ${WEEKDAY[d.getUTCDay()]}` : s;
}

/** 今天 · 昨天 · 9月15日 */
export function dayWord(n: number): string {
  const t = todayNo();
  if (n === t) return "今天";
  if (n === t - 1) return "昨天";
  return dateOfDay(n);
}

/** 36 分钟 · 4 小时 · 2 天: how long ago, without the 前. */
export function sinceWord(iso?: string): string {
  const t = iso ? Date.parse(iso) : NaN;
  if (Number.isNaN(t)) return "";
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 60) return `${Math.max(1, mins)} 分钟`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} 小时`;
  return `${Math.round(hours / 24)} 天`;
}

export type Net = "whatsapp" | "messenger";

export const netOf = (r: { network?: string; wa_id?: string }): Net =>
  r.network === "messenger" || (r.wa_id ?? "").startsWith("fb:") ? "messenger" : "whatsapp";

/** Search ignores spaces and dashes, so 012-345 6789 finds 0123456789. */
export const squash = (s: string) => s.toLowerCase().replace(/[\s-]/g, "");

/**
 * Marked 成交, or a sale recorded here, with no order in the shop: the sale was
 * never written there, so no report, no parcel check and no Meta hears of it.
 * Four of the tracker's sales from early September were exactly this.
 *
 * An order on the same customer's other chat is the order: Tan Yin Ying's
 * RM1,002 sat on the WhatsApp chat opened with the number she typed on
 * Messenger, and her Messenger row asked for a second one (2026-10-10).
 */
export const needsOrder = (r: Pick<LeadRow, "internal" | "purchased" | "status" | "orders" | "partner_orders">) =>
  !r.internal && (!!r.purchased || r.status === "won") && !((r.orders ?? 0) > 0) && !((r.partner_orders ?? 0) > 0);

// --- ads ------------------------------------------------------------------

const CAMPAIGN = /^\s*(\d+)\s*\(\s*([^)]+?)\s*\)\s*(.*)$/;
const COPY = /\s*-\s*copy\s*$/i;

/**
 * 「009 ( 猝死暴毙 ) 严重失眠会导致暴毙 / 猝死」 → 「009 猝死暴毙」. The team files
 * campaigns as number ( topic ) hook: the number and the topic are what tell two
 * apart, and next to the ad's picture the hook is only noise. Meta's 「- Copy」
 * becomes 副本. A name in any other shape is kept as it is.
 */
export function adLabel(campaign: string): string {
  const m = CAMPAIGN.exec(campaign);
  if (!m) return campaign.trim();
  return `${m[1]} ${m[2]}${COPY.test(m[3] ?? "") ? " 副本" : ""}`;
}

/**
 * An ad by the name the team knows it: its campaign's short name, else the ad's own — the headline it was tapped with, or
 * the name the ad sync read. An ad no synced spend files has no campaign, a Messenger one among them, and read only 「广告」
 * on the meeting-room screen and on 今天 while 对话 had its name (owner, 2026-10-31). "" for neither; a 「…」 campaign is a
 * note, not a name.
 */
export function adCalled(campaign?: string, headline?: string): string {
  if (campaign && !campaign.startsWith("「")) return adLabel(campaign);
  return (headline ?? "").trim();
}

/** The hook after the topic: 「严重失眠会导致暴毙 / 猝死」. Empty for other names. */
export function adHook(campaign: string): string {
  const m = CAMPAIGN.exec(campaign);
  return m ? (m[3] ?? "").replace(COPY, "").trim() : "";
}

// --- dates ---------------------------------------------------------------

export type RangeKey = "today" | "yesterday" | "7" | "30" | "all" | "custom";

/** A period on the shop's calendar; from/to are only read for 自定. */
export interface DateRange {
  key: RangeKey;
  from: string;
  to: string;
}

export const ALL_DATES: DateRange = { key: "all", from: "", to: "" };

export const RANGE_KEYS: Array<[RangeKey, string]> = [
  ["today", "今天"],
  ["yesterday", "昨天"],
  ["7", "7 天"],
  ["30", "30 天"],
  ["all", "全部"],
  ["custom", "自定"],
];

/** The first and last shop day of a period, both included; null for all time. */
export function rangeDays(r: DateRange): [number, number] | null {
  const t = todayNo();
  switch (r.key) {
    case "today":
      return [t, t];
    case "yesterday":
      return [t - 1, t - 1];
    case "7":
      return [t - 6, t];
    case "30":
      return [t - 29, t];
    case "custom": {
      const a = dateInputDay(r.from);
      const b = dateInputDay(r.to);
      if (Number.isNaN(a) && Number.isNaN(b)) return null;
      return [Number.isNaN(a) ? -Infinity : a, Number.isNaN(b) ? Infinity : b];
    }
    default:
      return null;
  }
}

export function inRange(iso: string | undefined, r: DateRange): boolean {
  const days = rangeDays(r);
  if (!days) return true;
  const n = dayNo(iso);
  return !Number.isNaN(n) && n >= days[0] && n <= days[1];
}

// --- who is still being sold to ---------------------------------------------

export const CLOSED = new Set(["won", "cancelled", "invalid", "agent", "lost"]);

/**
 * A buyer asking again: they bought, and tapped an ad after their latest sale
 * — the 谷粮 customer who taps the 排毒 ad is asking about 排毒, a new enquiry
 * counted from the tap, not a finished sale (owner, 2026-10-19). A sale after
 * the tap closes them again. The API's AskingAgain, by the same two times.
 */
export function askingAgain(r: Pick<LeadRow, "ad_clicked_at" | "last_order_at">): boolean {
  const tapped = Date.parse(r.ad_clicked_at ?? "");
  const sold = Date.parse(r.last_order_at ?? "");
  return !Number.isNaN(tapped) && !Number.isNaN(sold) && tapped > sold;
}

/** Bought, and not asking again since: the sale is the end of it for now. */
export const doneBuying = (r: Pick<LeadRow, "purchased" | "ad_clicked_at" | "last_order_at">) => !!r.purchased && !askingAgain(r);

export const isOpen = (r: LeadRow) => !doneBuying(r) && !CLOSED.has(r.status || "new");

// --- the day's work --------------------------------------------------------

export type StepKey = "reply" | "close" | "chase" | "ship" | "aftersale";
export type Tone = "red" | "amber" | "blue" | "green";
/** A work list counted by the customer: its filter on 对话's list and its key in /leads/counts. */
export type WorkList = "reply" | "close" | "chase" | "aftersale_due";

export interface Step {
  key: StepKey;
  title: string;
  /** The step in a word, where a row names it: 今天's 「回复 · Eve」. */
  word: string;
  icon: ShellIconName;
  /** Whose job it is: 今天 puts it on 客服's card or on Admin's. */
  lane: "cs" | "admin";
  /** Its work list (对话's buttons, the server's rules). 安排发货 has none: it counts orders, not customers. */
  list?: WorkList;
  /** One line under the title: what the list is. */
  hint: string;
  /** The button on each row. */
  cta: string;
  tone: Tone;
  /** Whether this customer is on it. For 安排发货, whether their newest order waits (one customer's card);
   *  its list is every order waiting (shipList). */
  on: (r: LeadRow) => boolean;
}

/**
 * Wanted to buy: asked the price or how to pay (the rules' 合格), or a
 * colleague marked them 考虑中 or 待付款. Chasing them comes first. The server
 * decides (hot), so the chat list's buttons count the same lists; the rule
 * here only stands in for an older API.
 */
export const hot = (r: LeadRow) =>
  r.hot ?? (r.lead_stage === "qualified" || r.status === "considering" || r.status === "awaiting_payment");

/**
 * WhatsApp at this customer — the app on the phone, or WhatsApp Web — which is
 * where the team writes: 2,662 of 2,675 colleague messages 9/16–9/22 were typed
 * there, and after a day's silence only the app may send more than a paid
 * template. "" for a chat whose id is not a number.
 */
export const waLink = (r: Pick<LeadRow, "wa_id">, text = "") =>
  /^\d{8,15}$/.test(r.wa_id) ? `https://wa.me/${r.wa_id}${text ? `?text=${encodeURIComponent(text)}` : ""}` : "";

/**
 * A 回访's WhatsApp link: their own chat, or for one with no number (a Messenger
 * person) the number their newest order is under. Days after they last wrote,
 * Messenger takes nothing from us, so the phone is the only way to them (owner,
 * 2026-11-04: 「修」, after a parcel came and no button could reach its buyer).
 */
export const aftersaleLink = (r: Pick<LeadRow, "wa_id" | "last_order">, text: string) =>
  waLink(r, text) || waLink({ wa_id: (r.last_order?.phone ?? "").replace(/\D/g, "") }, text);

/**
 * A 回访 in words (api attribution.AfterSaleArrived / AfterSaleFinished): which
 * of the two, when the parcel came, what to ask, and the line the phone opens
 * with — short, for the customers we have (owner, 2026-10-08: 「包裹送达后提醒cs
 * follow up他们是否收到包裹了 然后想教他们怎么服用…30天后…问他们还要不要回购
 * 或推他们新促销」).
 */
export function afterSaleWords(r: Pick<LeadRow, "aftersale_checkpoint" | "aftersale_day">): {
  tag: string;
  tone: "green" | "amber";
  when: string;
  ask: string;
  line: string;
} {
  const day = r.aftersale_day ?? 0;
  if (r.aftersale_checkpoint === 30) {
    return {
      tag: "快吃完了",
      tone: "amber",
      when: `送到 ${day} 天了`,
      ask: "问还要不要，推现在的优惠",
      line: "您好😊 之前买的应该快吃完了吧？要不要帮你再安排一份？现在有优惠哦",
    };
  }
  return {
    tag: "刚收到货",
    tone: "green",
    when: day <= 0 ? "今天送到" : day === 1 ? "昨天送到" : `${day} 天前送到`,
    ask: "问收到没，教怎么吃",
    line: "您好😊 包裹收到了吗？要怎么吃，我教你哦",
  };
}

/** Why a 快成交 customer is one, in a word. */
export const hotWord = (r: LeadRow) => (r.status === "awaiting_payment" ? "待付款" : r.status === "considering" ? "考虑中" : "问过价");

/** The days a customer is chased on, after the day they last wrote (api attribution.ChaseOnDay). */
export const CHASE_ON_DAY = [1, 3, 6];

/**
 * Where a customer stands in being chased, in words: 「客人 3 天没回 · 追了 1 次」
 * and what comes next. The server decides whether a chase is due today; this
 * only says it.
 */
export function chaseWords(r: LeadRow): { state: string; next: string; tone: "red" | "amber" | "grey" | "" } {
  // An older API sends no quiet_days: say nothing rather than guess.
  if (r.quiet_days === undefined || doneBuying(r) || CLOSED.has(r.status || "new")) return { state: "", next: "", tone: "" };
  const q = r.quiet_days;
  const n = r.chases ?? 0;
  const tried = n === 0 ? "还没追" : `追了 ${n} 次`;
  // 小爱 handed it over: it is a person's job, not a reply to write (owner, 2026-10-11, of a chat she had
  // answered that still said 等你回: 「小爱不是回了吗？」).
  if (r.needs_human) return { state: "小爱交给真人", next: "要真人", tone: "red" };
  if (r.unanswered) return { state: q > 0 ? `客户 ${q} 天前说的` : "客户刚说完", next: "等你回", tone: "red" };
  if (r.status === "no_reply") return { state: q > 0 ? `客户 ${q} 天没回 · ${tried}` : "", next: "不追了", tone: "grey" };
  if (q < 0) return { state: "客户还没说过话", next: "", tone: "" };
  if (q === 0) return { state: "客户今天有说话", next: "", tone: "" };
  const state = `客户 ${q} 天没回 · ${tried}`;
  if ((r.chase_due ?? 0) > 0) return { state, next: `今天追第 ${r.chase_due} 次`, tone: "amber" };
  if (n >= CHASE_ON_DAY.length) return { state, next: "追满 3 次了，等客户回", tone: "grey" };
  if (q > 7) return { state, next: "7 天没回，不追了", tone: "grey" };
  const day = CHASE_ON_DAY[n] ?? Infinity;
  if (day > q) return { state, next: `第 ${day} 天追第 ${n + 1} 次`, tone: "" };
  return { state, next: "今天追过了", tone: "" };
}

/**
 * A chase due today. Given up on (不追了) is off it at once, as the server has
 * it (api ChaseDue: never 无回复): the row keeps its chase_due until it is read
 * again.
 */
const chaseDue = (r: LeadRow) => (r.chase_due ?? 0) > 0 && r.status !== "no_reply";

/** An order waiting to go out: paid, or cash on delivery, and not sent — 订单's 待发货. */
export const waitsToShip = (o: OrderBrief) => orderStage(o) === "to_ship";

/**
 * The day's work in the order it should be done, as things rather than people:
 * one customer can be waiting for a reply AND have a missed day, and showing
 * them once under the first step hid the second job — a delivered
 * cash-on-delivery order hid behind 「回复客人」 that way, RM316 of it (owner,
 * 2026-09-20). Money left this list the same day: it is counted by the order
 * on 要收的钱, where two orders of one customer are two lines; 安排发货 is
 * counted by the order too (10/24): a paid order behind its customer's newer
 * one was on no list at all.
 *
 * The only list of the steps: the chat list's buttons (LeadsView QUICK), 今天's
 * two cards and 客户表's 今天要做 all take their names, marks and rules from
 * here, so a title changed here changes everywhere.
 */
export const STEPS: Step[] = [
  {
    key: "reply",
    title: "回复客户",
    word: "回复",
    icon: "chat",
    lane: "cs",
    list: "reply",
    hint: "客户发了消息还没人回，等最久的排在最前面",
    cta: "去回复",
    tone: "red",
    on: (r) => !!r.unanswered || !!r.needs_human,
  },
  // Chasing replaced 「补跟进」 and 「今天跟进」, which asked for a message to
  // every customer on every day of their first week, answered or not: on
  // production 9/8–9/22 the first chase of a quiet customer was answered 22%
  // of the time, the third 5%, after a week 0% (owner, 2026-09-23: 「最多追 3
  // 次、7 天不回就停」).
  {
    key: "close",
    title: "快成交",
    word: "快成交",
    icon: "flame",
    lane: "cs",
    list: "close",
    hint: "问过价钱、想买的客户不回了，今天追一次",
    cta: "去追",
    tone: "red",
    on: (r) => chaseDue(r) && hot(r),
  },
  {
    key: "chase",
    title: "追一次",
    word: "追一次",
    icon: "send",
    lane: "cs",
    list: "chase",
    hint: "客户不回：第 1、3、6 天各追一次，7 天不回就不追了",
    cta: "去追",
    tone: "amber",
    on: (r) => chaseDue(r) && !hot(r),
  },
  {
    key: "ship",
    title: "安排发货",
    word: "发货",
    icon: "truck",
    lane: "admin",
    hint: "付了款或货到付款、还没发出去的单，等最久的排在最前面",
    cta: "去发货",
    tone: "blue",
    on: (r) => !!r.last_order && waitsToShip(r.last_order),
  },
  {
    key: "aftersale",
    title: "回访",
    word: "回访",
    icon: "heart",
    lane: "cs",
    list: "aftersale_due",
    hint: "包裹送到了：问收到没、教怎么吃 · 送到满 30 天：问还要不要",
    cta: "去问",
    tone: "green",
    on: (r) => !!r.aftersale_due,
  },
];

/** The steps counted by the customer this one is on — a person can owe two different jobs. 安排发货 is counted by the order (shipList). */
export const stepsOf = (r: LeadRow): Step[] => STEPS.filter((s) => s.list && s.on(r));

/** The first of them, for anything that needs one word for a customer. */
export const stepOf = (r: LeadRow): Step | undefined => STEPS.find((s) => s.on(r));

const at = (iso?: string) => {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isNaN(t) ? 0 : t;
};

/** The order inside a step: whoever has waited longest, or is most urgent, first. */
/**
 * 认领 (owner, 2026-09-24): 「谁按了「手机打开」，那一行就标「某某在处理」，其他人
 * 跳过。」 A row somebody else opened within the half hour is theirs, and sinks
 * to the end of its step so the next colleague starts on one nobody has.
 */
export const takenByOther = (r: LeadRow) => !!r.claimed_by && !r.claimed_mine;
export const takenLast = (a: LeadRow, b: LeadRow) => Number(takenByOther(a)) - Number(takenByOther(b));

/** What a colleague opened the chat for, in the list's words; the audit log keeps it. */
export function claimFor(key: StepKey, r: LeadRow): string {
  switch (key) {
    case "reply":
      return "回复客户";
    case "close":
      return `快成交，追第 ${r.chase_due ?? 1} 次`;
    case "chase":
      return `追第 ${r.chase_due ?? 1} 次`;
    case "aftersale":
      return r.aftersale_checkpoint === 30 ? "回访：问还要不要" : "回访：问收到没";
    default:
      return "";
  }
}

export function stepSort(key: StepKey): (a: LeadRow, b: LeadRow) => number {
  switch (key) {
    case "reply":
      return (a, b) => at(a.last_in_at ?? a.last_activity_at) - at(b.last_in_at ?? b.last_activity_at);
    // The first chase is answered most often, so first chases first; the
    // customer who went quiet most recently first among them.
    case "close":
    case "chase":
      return (a, b) => (a.chase_due ?? 0) - (b.chase_due ?? 0) || (a.quiet_days ?? 0) - (b.quiet_days ?? 0);
    default:
      return (a, b) => at(b.last_activity_at) - at(a.last_activity_at);
  }
}

/** Since when an order has waited to go out: its payment, or a cash-on-delivery order's opening. */
export const shipSince = (o: OrderBrief) => o.paid_at ?? o.ordered_at;

/** 安排发货: every order waiting to go out, one job an order, the one waiting longest first. */
export function shipList(orders: readonly OrderBrief[]): OrderBrief[] {
  return orders.filter(waitsToShip).sort((a, b) => at(shipSince(a)) - at(shipSince(b)));
}

export type TodoCounts = Record<StepKey, number> & { all: number };

/**
 * 今天要做 in numbers: each step's, and all of them in jobs (件) — a customer
 * owing two is two, an order waiting to go out is one. The menu's 今天, 今天's
 * two cards, 客户表's tab and its 今天要做 line are this one sum; the menu once
 * said 13 and then 14 when 今天 was opened, 客户表 11 and 今天 9 + 5.
 *
 * The customer steps come from `people`: /leads/counts (对话's buttons, both
 * channels, the server's rules), or the rows on screen by the same rules, our
 * own numbers left out as the server leaves them. 安排发货 is the orders.
 */
export function todoCounts(people: LeadRow[] | Record<string, number>, orders: readonly OrderBrief[]): TodoCounts {
  const out = { all: 0 } as TodoCounts;
  for (const s of STEPS) {
    const n = !s.list
      ? orders.filter(waitsToShip).length
      : Array.isArray(people)
        ? people.filter((r) => !r.internal && s.on(r)).length
        : (people[s.list] ?? 0);
    out[s.key] = n;
    out.all += n;
  }
  return out;
}

/** 客户表's tab, remembered across visits (CustomersView). */
export const CUSTOMERS_TAB_KEY = "lazior_customers_tab";

/**
 * Open 客户表 on a tab from elsewhere (今天's links): saved for a first visit,
 * and told to a 客户表 already open, which keeps its tab otherwise.
 */
export function askCustomersTab(tab: "todo" | "money" | "all" | "orders"): void {
  try {
    localStorage.setItem(CUSTOMERS_TAB_KEY, tab);
  } catch {
    /* the open page still hears it */
  }
  window.dispatchEvent(new CustomEvent("customers:tab", { detail: tab }));
}
